import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const CANONICAL = 'https://hoosierlifeinsurance.com/whole-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

const FAQS = [
  {
    q: 'What is whole life insurance in Indianapolis?',
    a: 'Whole life insurance in Indianapolis is a permanent life insurance policy that covers you for your entire life — it never expires. Unlike term life insurance, which ends after 10–30 years, whole life insurance builds guaranteed cash value over time that you can borrow against tax-free. Indianapolis residents use it for estate planning, legacy building, business succession, and supplemental retirement income. Premiums are fixed for life and never increase.',
  },
  {
    q: 'How much does whole life insurance cost in Indianapolis?',
    a: 'Whole life insurance in Indianapolis typically costs $100–$500/month depending on your age, health, and coverage amount. A healthy 35-year-old Indianapolis resident can get $500,000 of whole life coverage for approximately $250–$350/month. A 45-year-old can expect to pay $400–$600/month for the same coverage. Rates are locked in at the time of purchase and never increase — making it valuable to buy when you\'re young and healthy.',
  },
  {
    q: 'How does whole life insurance cash value work in Indiana?',
    a: 'Part of every whole life premium builds cash value in a tax-deferred account that grows at a guaranteed rate (typically 2–4% annually). After 10 years, a $500/month policy typically has $42,000 in cash value. After 20 years, approximately $115,000. After 30 years, approximately $225,000 — exceeding total premiums paid. Indiana has no state tax on cash value growth, making it one of the most tax-efficient wealth-building tools available to Indianapolis residents.',
  },
  {
    q: 'Can I borrow against my whole life insurance policy in Indianapolis?',
    a: 'Yes. Once your whole life policy has accumulated sufficient cash value (typically after 2–3 years), you can take a policy loan at a low interest rate — usually 5–8%. Unlike a bank loan, there\'s no credit check, no approval process, and no required repayment schedule. Indianapolis residents use policy loans for home renovations, college tuition, business investments, and emergencies. The loan is not taxable, and your cash value continues to grow even while borrowed.',
  },
  {
    q: 'Is whole life insurance or term life insurance better for Indianapolis families?',
    a: 'It depends on your goals. Term life insurance is better for most Indianapolis families who need maximum income replacement at the lowest cost during their working years — a $500,000 term policy costs $25–$40/month vs. $250–$350/month for whole life. Whole life insurance is better for Indianapolis residents focused on estate planning, business succession, building tax-free wealth, or those who want guaranteed lifetime coverage. Many Indianapolis families use both: term life for income replacement and whole life for wealth building.',
  },
  {
    q: 'What are the best whole life insurance companies in Indianapolis?',
    a: 'As an independent Indianapolis insurance agency, Hoosier Life Insurance compares whole life rates from multiple A-rated carriers. Top whole life carriers for Indianapolis residents include: Northwestern Mutual and MassMutual (best for dividend-paying participating policies), Mutual of Omaha (best for final expense whole life), Protective Life and Banner Life (best for competitive non-participating rates), and North American Company (strong for business planning). The best carrier depends on your age, health, and goals.',
  },
  {
    q: 'How is whole life insurance used for estate planning in Indianapolis?',
    a: 'Indianapolis residents use whole life insurance for estate planning in several ways: (1) Providing immediate liquidity to pay estate settlement costs (funeral, probate, taxes) without forcing heirs to sell assets. (2) Equalizing inheritance when leaving a business or farm to one child — the other children receive the death benefit. (3) Funding an Irrevocable Life Insurance Trust (ILIT) to remove the death benefit from the taxable estate. Indiana has no state estate tax, so death benefits pass to heirs 100% income tax-free.',
  },
  {
    q: 'Can Indianapolis business owners use whole life insurance?',
    a: 'Yes. Indianapolis business owners use whole life insurance for: (1) Key person insurance — protects the business if a critical employee or owner dies. (2) Buy-sell agreements — funds business succession with guaranteed liquidity. (3) Executive bonus plans — attracts top talent with tax-advantaged benefits. (4) Business loan collateral — cash value can secure financing without personal guarantees. The death benefit is tax-free to the business, and cash value builds as a corporate asset.',
  },
  {
    q: 'What is a participating whole life insurance policy?',
    a: 'A participating whole life policy is issued by a mutual insurance company (like Northwestern Mutual, MassMutual, or New York Life) that pays annual dividends to policyholders. While dividends are not guaranteed, these carriers have paid them consistently for over 100 years. Dividends can be used to buy additional coverage, increase cash value, receive as cash, or reduce premiums. Participating policies typically build more cash value over time than non-participating policies.',
  },
  {
    q: 'Are whole life insurance benefits taxed in Indiana?',
    a: 'No. Indiana does not tax life insurance death benefits or cash value growth. Death benefits pass to beneficiaries 100% income tax-free (federal and state). Cash value grows tax-deferred, and policy loans are not taxable. Indiana also has no state estate tax. This makes whole life insurance one of the most tax-efficient wealth-building tools available to Indianapolis residents.',
  },
];

const RATE_TABLE = [
  { age: '30', male250: '$145/mo', female250: '$120/mo', male500: '$270/mo', female500: '$225/mo' },
  { age: '35', male250: '$175/mo', female250: '$145/mo', male500: '$325/mo', female500: '$270/mo' },
  { age: '40', male250: '$220/mo', female250: '$185/mo', male500: '$415/mo', female500: '$345/mo' },
  { age: '45', male250: '$285/mo', female250: '$240/mo', male500: '$540/mo', female500: '$455/mo' },
  { age: '50', male250: '$375/mo', female250: '$315/mo', male500: '$710/mo', female500: '$595/mo' },
];

const CASH_VALUE_TABLE = [
  { year: 'Year 5', paid: '$30,000', cv250: '$14,500', cv500: '$29,000' },
  { year: 'Year 10', paid: '$60,000', cv250: '$42,000', cv500: '$84,000' },
  { year: 'Year 15', paid: '$90,000', cv250: '$72,000', cv500: '$144,000' },
  { year: 'Year 20', paid: '$120,000', cv250: '$115,000', cv500: '$230,000' },
  { year: 'Year 30', paid: '$180,000', cv250: '$225,000', cv500: '$450,000' },
];

const NEIGHBORHOODS = [
  { name: 'Downtown Indianapolis', slug: 'indianapolis' },
  { name: 'Broad Ripple', slug: 'indianapolis' },
  { name: 'Carmel', slug: 'carmel' },
  { name: 'Fishers', slug: 'fishers' },
  { name: 'Noblesville', slug: 'noblesville' },
  { name: 'Westfield', slug: 'westfield' },
  { name: 'Greenwood', slug: 'indianapolis' },
  { name: 'Lawrence', slug: 'indianapolis' },
  { name: 'Castleton', slug: 'indianapolis' },
  { name: 'Geist', slug: 'indianapolis' },
  { name: 'Zionsville', slug: 'zionsville' },
  { name: 'Avon', slug: 'indianapolis' },
];

const TESTIMONIALS = [
  {
    name: 'Robert & Patricia H.',
    location: 'Carmel, IN',
    quote: 'We wanted permanent coverage that would also build wealth for our kids. Benjamin ran illustrations from 4 different carriers and explained every number clearly. We went with a participating policy and after 8 years the cash value has grown exactly as projected. Best financial decision we\'ve made.',
    stars: 5,
  },
  {
    name: 'Lisa M.',
    location: 'Fishers, IN',
    quote: 'As a business owner in Fishers, I needed key person insurance for my partner and myself. Hoosier Life structured whole life policies that protect our business and build corporate assets. The cash value has already helped us secure a business loan at a better rate.',
    stars: 5,
  },
  {
    name: 'James & Carol T.',
    location: 'Noblesville, IN',
    quote: 'We have a family farm and wanted to leave it to our son without shortchanging our daughter. Benjamin designed a whole life policy that equalizes the inheritance perfectly. Simple, elegant solution we never would have thought of on our own.',
    stars: 5,
  },
  {
    name: 'Thomas R.',
    location: 'Indianapolis, IN',
    quote: 'I wanted a policy that would never expire and could supplement my retirement income. Benjamin showed me how whole life cash value grows tax-deferred and can be accessed through policy loans. After 12 years, I have over $180K in cash value. Best financial decision I ever made.',
    stars: 5,
  },
];

const USE_CASES = [
  {
    icon: 'ri-file-shield-line',
    title: 'Estate Planning',
    desc: 'Provide immediate liquidity for estate settlement costs, equalize inheritance between heirs, and pass wealth to the next generation 100% income tax-free. Indiana has no state estate tax.',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Business Succession',
    desc: 'Fund buy-sell agreements, protect against loss of key employees, and build corporate assets. Indianapolis business owners use whole life for guaranteed succession planning.',
  },
  {
    icon: 'ri-bank-line',
    title: 'Tax-Free Wealth Building',
    desc: 'Cash value grows tax-deferred and policy loans are not taxable. After 20–30 years, your cash value can exceed total premiums paid — a powerful supplement to your 401(k) and IRA.',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'College Funding',
    desc: 'Whole life cash value is not counted as an asset on the FAFSA, protecting financial aid eligibility. Take tax-free policy loans to fund tuition at IU, Purdue, Butler, or IUPUI.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Final Expense Coverage',
    desc: 'Guarantee your family never faces a $9,000–$14,000 funeral bill. Whole life never expires, so coverage is there when your family needs it most — no matter when that is.',
  },
  {
    icon: 'ri-hand-coin-line',
    title: 'Retirement Supplement',
    desc: 'Access cash value through tax-free policy loans in retirement. Unlike a 401(k), there are no required minimum distributions and no market risk on your guaranteed cash value.',
  },
];

export default function WholeLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'term' | 'whole'>('whole');

  useEffect(() => {
    const title = 'Whole Life Insurance Indianapolis IN | Permanent Coverage + Cash Value | Hoosier Life';
    const description = 'Whole life insurance in Indianapolis, IN — lifetime coverage that builds guaranteed cash value. Estate planning, business succession, tax-free wealth building. Independent agent, 20+ carriers. Call (317) 648-2983.';
    const keywords = 'whole life insurance Indianapolis, whole life insurance Indianapolis IN, Indianapolis whole life insurance, permanent life insurance Indianapolis, cash value life insurance Indianapolis, whole life insurance Marion County Indiana';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/a88b52ee546628e437ec7d97ead797fc.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: new Date().toISOString(),
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Whole Life Insurance', item: `${SITE_URL}/whole-life` },
          { '@type': 'ListItem', position: 3, name: 'Whole Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Whole Life Insurance',
        name: 'Whole Life Insurance Indianapolis',
        description: 'Permanent whole life insurance for Indianapolis and Marion County residents. Lifetime coverage with guaranteed cash value growth, fixed premiums, and tax-free death benefits. Independent agent comparing 20+ A-rated carriers.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '94',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${CANONICAL}#speakable`,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '.key-takeaways', '#faq'],
          xpath: [
            '/html/head/title',
            '/html/head/meta[@name=\'description\']/@content',
          ],
        },
        url: CANONICAL,
        name: 'Whole Life Insurance Indianapolis IN | Permanent Coverage + Cash Value | Hoosier Life',
        description: 'Whole life insurance in Indianapolis, IN — lifetime coverage that builds guaranteed cash value. Estate planning, business succession, tax-free wealth building. Independent agent, 20+ carriers. Call (317) 648-2983.',
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/whole-life" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Whole Life Insurance
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Indianapolis</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=happy%20multi-generational%20family%20grandparents%20parents%20and%20grandchildren%20gathered%20in%20a%20bright%20warm%20modern%20American%20living%20room%20Indianapolis%20Indiana%20representing%20whole%20life%20insurance%20legacy%20planning%20and%20wealth%20transfer%20professional%20lifestyle%20photography%20warm%20natural%20light%20cream%20and%20beige%20tones%20soft%20bokeh%20background&width=1440&height=700&seq=wl-indy-hero-2025a&orientation=landscape"
            alt="Whole Life Insurance Indianapolis Indiana — Permanent Coverage with Guaranteed Cash Value"
            title="Whole Life Insurance in Indianapolis, Indiana — Lifetime Coverage + Cash Value"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-[660px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-map-pin-fill text-sm"></i>
              Serving Indianapolis &amp; Marion County
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Whole Life Insurance —{' '}
              <span className="text-[#D97642]">Permanent Coverage for Indianapolis Families</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-[580px]">
              Permanent coverage that <strong className="text-white">never expires</strong> — and builds real, tax-free wealth you can access while you're still alive. Local Indianapolis agent comparing 20+ A-rated carriers to find your best rate.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-infinity-line', text: 'Never expires' },
                { icon: 'ri-line-chart-line', text: 'Guaranteed cash value' },
                { icon: 'ri-lock-line', text: 'Fixed premiums for life' },
                { icon: 'ri-map-pin-fill', text: 'Local Indianapolis agent' },
              ].map(badge => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
                  <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-xs text-white font-semibold whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowBookingModal(true)}
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-check-line text-lg"></i>
                Get My Free Quote
              </button>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$250K+', label: 'Avg. cash value at age 65 (30-yr policy)' },
              { value: '100%', label: 'Tax-free death benefit — no Indiana state tax' },
              { value: '20+', label: 'A-rated carriers compared for your best rate' },
              { value: '12 yrs', label: 'Benjamin Wright\'s experience serving Indianapolis' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Whole Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Whole life insurance NEVER expires — your family is covered for life as long as premiums are paid',
                  'Cash value grows at a guaranteed 2–4% annually — tax-deferred, no market risk',
                  'After 20 years, cash value nearly equals total premiums paid — after 30 years it exceeds them',
                  'Policy loans are tax-free — use cash value for emergencies, college, retirement, or business',
                  'Indiana has no state estate tax — death benefits pass to heirs 100% income tax-free',
                  'Premiums are fixed for life — locked in at the age you buy, never increase',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content + Sticky Sidebar */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* How It Works */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">How It Works</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How Whole Life Insurance Works in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Think of it this way: term life is renting protection. Whole life insurance is buying it — and it builds equity you can actually use while you're still alive. Every premium payment does two things simultaneously.
              </p>
              <div className="grid sm:grid-cols-2 gap-5 mb-8">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    color: '#D97642',
                    title: 'Death Benefit Protection',
                    desc: 'Your family receives a guaranteed, tax-free lump sum when you pass away — no matter when that is. The death benefit never decreases and never expires.',
                  },
                  {
                    icon: 'ri-line-chart-line',
                    color: '#1A3A52',
                    title: 'Cash Value Growth',
                    desc: 'Part of every premium builds a tax-deferred savings account growing at a guaranteed 2–4% annually. This is real money you own and can access anytime.',
                  },
                  {
                    icon: 'ri-lock-line',
                    color: '#D97642',
                    title: 'Fixed Premiums for Life',
                    desc: 'Your premium is locked in the day you buy the policy and never increases — even as you age or your health changes. Buy young, lock in low rates forever.',
                  },
                  {
                    icon: 'ri-hand-coin-line',
                    color: '#1A3A52',
                    title: 'Tax-Free Policy Loans',
                    desc: 'Borrow against your cash value at 5–8% interest with no credit check, no approval, and no required repayment. The loan is not taxable income.',
                  },
                ].map(item => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${item.color}15` }}>
                      <i className={`${item.icon} text-2xl`} style={{ color: item.color }}></i>
                    </div>
                    <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Indiana Tax Advantage */}
              <div className="bg-[#1A3A52] rounded-2xl p-7">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-[#D97642]/20">
                    <i className="ri-government-line text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-2">Indiana Tax Advantage: No State Tax on Whole Life Benefits</h3>
                    <p className="text-sm text-white/80 leading-relaxed">
                      Indiana does not tax life insurance death benefits or cash value growth. Death benefits pass to beneficiaries <strong className="text-white">100% income tax-free</strong> (federal and state). Cash value grows tax-deferred, and policy loans are not taxable. Indiana also has <strong className="text-white">no state estate tax</strong> — making whole life insurance one of the most tax-efficient wealth-building tools available to Indianapolis residents.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rate Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Sample Rates</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Whole Life Insurance Rates in Indianapolis (2025)
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Approximate monthly premiums for non-smokers in standard health. Rates are locked in at purchase and never increase. Female rates are typically 10–15% lower.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      <th className="px-4 py-3 text-left font-semibold">Age</th>
                      <th className="px-4 py-3 text-center font-semibold">Male $250K</th>
                      <th className="px-4 py-3 text-center font-semibold">Female $250K</th>
                      <th className="px-4 py-3 text-center font-semibold">Male $500K</th>
                      <th className="px-4 py-3 text-center font-semibold">Female $500K</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RATE_TABLE.map((row, i) => (
                      <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-4 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.male250}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.female250}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.male500}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.female500}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400">Rates are estimates only. Actual premiums depend on health, carrier, and coverage amount. Call (317) 648-2983 for your exact quote.</p>
            </div>

            {/* Cash Value Growth Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Cash Value Growth</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How Your Cash Value Grows Over Time in Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Based on a $500/month premium for a healthy 40-year-old Indianapolis resident. Guaranteed growth rates only — participating policies may earn additional dividends.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      <th className="px-4 py-3 text-left font-semibold">Policy Year</th>
                      <th className="px-4 py-3 text-center font-semibold">Total Premiums Paid</th>
                      <th className="px-4 py-3 text-center font-semibold">$250K Policy Cash Value</th>
                      <th className="px-4 py-3 text-center font-semibold">$500K Policy Cash Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CASH_VALUE_TABLE.map((row, i) => (
                      <tr key={row.year} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-4 py-3 font-semibold text-[#1A3A52]">{row.year}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.paid}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.cv250}</td>
                        <td className={`px-4 py-3 text-center font-semibold ${i >= 3 ? 'text-[#D97642]' : 'text-gray-600'}`}>{row.cv500}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-[#FFF4ED] border-l-4 border-[#D97642] rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-xl text-[#D97642] flex-shrink-0 mt-0.5"></i>
                  <div>
                    <h4 className="text-sm font-bold text-[#1A3A52] mb-1">The Break-Even Point</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      By year 20, your cash value nearly equals total premiums paid. After year 30, it <strong>exceeds</strong> what you've paid in — and you still have the full death benefit. This is why whole life insurance is a wealth-building tool, not just insurance.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Term vs Whole Life Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Whole Life vs. Term Life Insurance in Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Both protect your family — but they work very differently. Here's an honest comparison for Indianapolis residents.
              </p>

              {/* Tab Switcher */}
              <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-6 w-fit">
                <button
                  onClick={() => setActiveTab('whole')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'whole' ? 'bg-[#1A3A52] text-white' : 'text-gray-600 hover:text-[#1A3A52]'}`}
                >
                  Whole Life
                </button>
                <button
                  onClick={() => setActiveTab('term')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activeTab === 'term' ? 'bg-[#1A3A52] text-white' : 'text-gray-600 hover:text-[#1A3A52]'}`}
                >
                  Term Life
                </button>
              </div>

              {activeTab === 'whole' && (
                <div className="bg-white rounded-2xl border-2 border-[#D97642]/30 p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-[#D97642] rounded-xl flex items-center justify-center">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1A3A52]">Whole Life Insurance</h3>
                      <p className="text-sm text-gray-500">Permanent coverage + guaranteed cash value</p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Coverage Duration', value: 'Lifetime — never expires' },
                      { label: 'Cash Value', value: 'Guaranteed 2–4% annual growth' },
                      { label: 'Premiums', value: 'Fixed for life, never increase' },
                      { label: 'Market Risk', value: 'Zero — fully guaranteed' },
                      { label: 'Policy Loans', value: 'Tax-free, no credit check' },
                      { label: 'Dividends', value: 'Available from mutual carriers' },
                      { label: 'Best For', value: 'Estate planning, wealth building, business' },
                      { label: 'Sample Cost', value: '$250–$500/mo for $500K (age 35–45)' },
                    ].map(item => (
                      <div key={item.label} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-[#D97642] mt-0.5 flex-shrink-0"></i>
                        <div>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{item.label}</span>
                          <p className="text-sm text-[#1A3A52] font-medium">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'term' && (
                <div className="bg-white rounded-2xl border-2 border-gray-200 p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center">
                      <i className="ri-calendar-check-line text-2xl text-gray-600"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1A3A52]">Term Life Insurance</h3>
                      <p className="text-sm text-gray-500">Affordable coverage for a set period</p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Coverage Duration', value: '10, 20, or 30 years — then expires' },
                      { label: 'Cash Value', value: 'None — pure protection only' },
                      { label: 'Premiums', value: 'Fixed during term, much higher to renew' },
                      { label: 'Market Risk', value: 'None' },
                      { label: 'Policy Loans', value: 'Not available — no cash value' },
                      { label: 'Dividends', value: 'Not available' },
                      { label: 'Best For', value: 'Income replacement, mortgage protection' },
                      { label: 'Sample Cost', value: '$25–$40/mo for $500K (age 35, 20-yr)' },
                    ].map(item => (
                      <div key={item.label} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-gray-400 mt-0.5 flex-shrink-0"></i>
                        <div>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{item.label}</span>
                          <p className="text-sm text-[#1A3A52] font-medium">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 bg-[#F9F7F4] rounded-xl p-5 border border-gray-200">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-xl text-[#1A3A52] flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong className="text-[#1A3A52]">Our honest take:</strong> Most Indianapolis families benefit from <em>both</em> — a large term life policy for income replacement during working years, plus a smaller whole life policy for permanent coverage and wealth building. We'll show you exactly how to structure both for maximum value.
                  </p>
                </div>
              </div>
            </div>

            {/* Use Cases */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Who It's For</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Who Uses Whole Life Insurance in Indianapolis?
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {USE_CASES.map(item => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-2xl text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Reviews</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                What Indianapolis Residents Are Saying About Whole Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Neighborhoods */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Service Areas</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Whole Life Insurance Serving Indianapolis &amp; Marion County
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We provide whole life insurance consultations to Indianapolis residents throughout Marion County and surrounding communities — all by phone or video, no office visit required.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {NEIGHBORHOODS.map(n => (
                  <Link
                    key={n.name}
                    to={`/service-areas/${n.slug}`}
                    className="bg-white hover:bg-[#D97642] hover:text-white border border-gray-200 rounded-lg p-4 text-center transition-all group cursor-pointer"
                  >
                    <i className="ri-map-pin-line text-xl text-[#D97642] group-hover:text-white mb-2 block"></i>
                    <div className="text-sm font-medium text-gray-700 group-hover:text-white">{n.name}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Whole Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-wl-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-shield-check-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Indianapolis Whole Life Quote</h3>
                    <p className="text-xs text-white/70">Marion County · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Cash value illustrations</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Results in 24 hours</p>
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Strategy Call</h3>
                    <p className="text-xs text-white/70">We'll run your personalized cash value illustration</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/whole-life', label: 'Whole Life Insurance Indiana', icon: 'ri-shield-check-line' },
                  { to: '/term-life-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/life-insurance-indianapolis', label: 'Life Insurance Indianapolis', icon: 'ri-map-pin-line' },
                  { to: '/blog/whole-life-insurance-indianapolis', label: 'Whole Life Guide (Blog)', icon: 'ri-article-line' },
                  { to: '/blog/term-vs-whole-life-indianapolis', label: 'Term vs. Whole Life Guide', icon: 'ri-scales-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20navy%20blue%20blazer%20and%20white%20shirt%20warm%20confident%20smile%20direct%20eye%20contact%20clean%20white%20studio%20background%20professional%20corporate%20headshot%20photography%20approachable%20trustworthy%20demeanor%20financial%20advisor%20portrait%20Indianapolis%20Indiana&width=600&height=700&seq=agent-bwright-wl-indy-2025c&orientation=portrait"
                alt="Benjamin Wright — Licensed Whole Life Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Whole Life Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Licensed Agent</div>
                    <div className="text-base font-bold text-[#1A3A52]">12 Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Agent
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                As a licensed insurance agent serving Indianapolis and Central Indiana for over 12 years, I've helped hundreds of Marion County residents build lasting wealth with whole life insurance — from first-time buyers to business owners planning succession.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                I run personalized cash value illustrations from 20+ A-rated carriers so you can see exactly what you'll build before you commit. No jargon, no pressure — just honest numbers and clear advice.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Schedule a Call with Benjamin
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore More Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Discover other insurance solutions that complement your whole life insurance strategy</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance Indiana', desc: 'Statewide whole life coverage — full product details, cash value calculator, and carrier comparison' },
              { to: '/term-life-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Indianapolis', desc: 'Affordable term life insurance for Indianapolis families from $15/mo' },
              { to: '/indexed-universal-life', icon: 'ri-line-chart-line', title: 'Indexed Universal Life (IUL)', desc: 'Market-linked growth with 0% floor — higher potential than whole life for younger buyers' },
              { to: '/wealth-strategy-call', icon: 'ri-calendar-check-line', title: 'Book Strategy Call', desc: 'Free consultation with a local Indianapolis insurance agent' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Build wealth while protecting your Indianapolis family — for life.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Get a free whole life insurance quote from a local Marion County agent — with a personalized cash value illustration showing exactly what you'll build.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowBookingModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-check-fill text-lg"></i>
              Get My Free Quote
            </button>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={94} productName="Whole Life Insurance Indianapolis" />
      <Footer />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Schedule Your Free Indianapolis Consultation</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
